// src/components/Explainer.js

import React from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";

// Icon Imports
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KingBedOutlinedIcon from "@mui/icons-material/KingBedOutlined";
import PhotoCameraBackIcon from "@mui/icons-material/PhotoCameraBack";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import SpeedIcon from "@mui/icons-material/Speed";
import TuneIcon from "@mui/icons-material/Tune";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FlightIcon from "@mui/icons-material/Flight";
import HotelIcon from "@mui/icons-material/Hotel";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const FeaturePoint = ({ Icon, title, description }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", mt: 3 }}>
    <Box sx={{ mr: 2, mt: 0.5, flexShrink: 0 }}>
      <Icon sx={{ color: "primary.main", fontSize: "2rem" }} />
    </Box>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mt: 0.5, lineHeight: 1.6 }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

const AnimatedFeatureSection = ({ feature, index }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const imageSide = isMobile ? "top" : index % 2 === 0 ? "right" : "left";

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <motion.div
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
    >
      <Box
        sx={{
          mt: { xs: 4, md: 0 },
          mb: { xs: 2, md: 0 },
          p: { xs: 0, md: 4 },
        }}
      >
        <Grid
          container
          spacing={isMobile ? 4 : 10}
          alignItems="center"
          direction={imageSide === "left" ? "row-reverse" : "row"}
        >
          {/* Text Column (50% width) */}
          <Grid item xs={12} md={6} sx={{ pb: { xs: 2, md: 0 } }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.stay} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {feature.title}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "text.secondary", lineHeight: 1.7 }}
            >
              {feature.subtitle}
            </Typography>
            <Box sx={{ mt: 3 }}>
              {feature.features.map((point, i) => (
                <FeaturePoint
                  key={i}
                  Icon={point.icon}
                  title={point.title}
                  description={point.description}
                />
              ))}
            </Box>
          </Grid>

          {/* Image Column (50% width) */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* ✅ CHANGE 1: This Box now reads size instructions from your data list */}
            <Box
              component="img"
              src={feature.image}
              alt={feature.imageAlt}
              sx={{
                // Bigger on mobile, keep your per-feature width on sm+
                width: { xs: "100%", sm: feature.imageWidth || "80%" },
                // Let it expand more on phones; cap higher on desktop
                maxWidth: { xs: "100%", md: "720px" },
                height: feature.imageHeight || "auto",
                objectFit: "contain",
                borderRadius: "16px",
                mx: "auto", // center horizontally
                display: "block",
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </motion.div>
  );
};

const Explainer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const iconStyles = {
    width: "70px",
    height: "70px",
    objectFit: "contain",
    marginBottom: "16px",
  };

  const symbolStyles = {
    fontSize: "40px",
    fontWeight: "bold",
    color: theme.palette.text.primary,
    mx: isMobile ? 1 : 2,
    my: isMobile ? 2 : 0,
  };

  // ✅ CHANGE 2: This is where you set the custom size for each image
  const featureData = [
    {
      id: "find-stays",
      image:
        theme.palette.mode === "dark"
          ? "/airbnb_tokyo_black.svg"
          : "/airbnb_tokyo_white.svg",
      imageAlt: "Stays Illustration",
      imageWidth: "80%",
      title: "Find Airbnbs That Fit",
      subtitle:
        "Search a city and 2,000 listings come back. These filters cut it down to the ones that are actually worth your time.",
      features: [
        {
          icon: PriceCheckIcon,
          title: "Smart Price Tiers",
          description:
            "Listings are automatically bucketed into Economy, Standard, and Premium — so you're comparing apples to apples at your price point.",
        },
        {
          icon: FilterAltOutlinedIcon,
          // ✅ New, more powerful title
          title: "Set Your Quality Standards",
          description:
            "Set a minimum rating and review count so you only see listings other travelers have actually vetted. The most reviewed stays float to the top.",
        },
        {
          icon: TuneIcon,
          title: "Filter by 90+ Amenities",
          // ✅ New, more compelling description
          description:
            "Go beyond the basics and search over 90 unique amenities. Find rare perks like a ping pong table or a record player to match your perfect trip.",
        },
      ],
    },
    // ✅ NEW COMBINED SECTION
    {
      id: "analyze-and-review",
      image:
        theme.palette.mode === "dark"
          ? "/review_black.svg"
          : "/review_white.svg", // Using your combined image
      imageAlt: "Analyze Listing with AI",
      imageWidth: "100%",
      imageHeight: "100%",
      title: "Get an AI-powered Second Opinion.",
      subtitle:
        "Get the real story behind a listing without reading a single review. Our AI analyzes guest feedback for you, summarizing the good and the bad in a clear, easy-to-digest format.",
      features: [
        {
          icon: ThumbUpOutlinedIcon,
          title: "Understand the Vibe",
          description:
            "Our AI reads the reviews for you, instantly highlighting the positive aspects that past guests consistently loved.",
        },
        {
          icon: ReportProblemOutlinedIcon,
          title: "Spot Potential Red Flags",
          description:
            "The AI analysis also identifies common complaints and potential issues, so you're aware of any tradeoffs before you book.",
        },
        {
          icon: FmdGoodOutlinedIcon,
          title: "Get All The Facts",
          description:
            "Confirm every detail at a glance—from the photo carousel and location map to the full amenity list and cancellation policy.",
        },
      ],
    },
    {
      id: "book-flights",
      image:
        theme.palette.mode === "dark"
          ? "/Flight_black.svg"
          : "/Flight_white.svg",
      imageAlt: "Flights Illustration",
      imageWidth: "80%",
      title: "Get Flight Costs Fast",
      subtitle:
        "Flight and stay results load at the same time. Pick your flight, book directly with the airline — no re-entering your details.",
      features: [
        {
          icon: SpeedIcon,
          title: "Quick Sorting Filters",
          description:
            "Instantly sort flight options by 'Best' (value), 'Cheapest', or 'Fastest' to suit your priorities.",
        },
        {
          icon: TravelExploreIcon,
          title: "Full Itinerary View",
          description:
            "See the complete travel plan, including stops, airline, and total duration.",
        },
        {
          icon: FlightTakeoffIcon,
          title: "Seamless Booking",
          description:
            "Book the flights directly from the airline without having to re-fill in your travel details.",
        },
      ],
    },
  ];

  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Box sx={{ textAlign: "center", mb: { xs: 4, md: 10 }, px: 2 }}>
        <Typography variant="h2" component="h2" sx={{ fontWeight: 800, mb: 2 }}>
          How FlyBnB Works
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 6 }}>
          Everything you need to stop tab-hopping and actually plan your trip.
        </Typography>

        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ maxWidth: "1000px", mx: "auto" }}
        >
          <Grid item xs={12} sm={3} sx={{ textAlign: "center" }}>
            <img src="/flight-icon.svg" alt="Flight Icon" style={iconStyles} />
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Flights
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
              Real flight prices, direct from the airlines. Sort by cheapest,
              fastest, or best value.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Typography sx={symbolStyles}>+</Typography>
          </Grid>
          <Grid item xs={12} sm={3} sx={{ textAlign: "center" }}>
            <img src="/stay-icon.svg" alt="Stay Icon" style={iconStyles} />
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Airbnb
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
              Airbnb stays sorted by price tier and review quality — no more
              scrolling through 2,000 listings.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Typography sx={symbolStyles}>=</Typography>
          </Grid>
          <Grid item xs={12} sm={3} sx={{ textAlign: "center" }}>
            <img
              src="/both-icon.svg"
              alt="FlyBnB Icon"
              style={{ ...iconStyles, width: "80px", height: "80px" }}
            />
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              FlyBnB
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
              One number: your full trip cost. Because the cheap flight is often
              the expensive trip.
            </Typography>
          </Grid>
        </Grid>

        {/* CTA after intro */}
        <Box
          sx={{
            mt: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() =>
                window.scrollTo({ top: 0, behavior: "smooth" })
              }
              variant="contained"
              size="large"
              sx={{
                fontWeight: 800,
                fontSize: "1rem",
                py: 1.6,
                px: { xs: 3, md: 5 },
                color: "#fff",
                borderRadius: "50px",
                textTransform: "none",
                background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                boxShadow: "0 8px 24px rgba(95, 90, 162, 0.35)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px) scale(1.02)",
                  boxShadow: "0 12px 32px rgba(95, 90, 162, 0.5)",
                },
              }}
            >
              Start Searching Free
            </Button>
            <Button
              onClick={() => {
                const el = document.getElementById("subscribe-section");
                if (el)
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              variant="outlined"
              size="large"
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                py: 1.6,
                px: { xs: 3, md: 5 },
                borderRadius: "50px",
                textTransform: "none",
                borderColor: theme.palette.divider,
                color: "text.primary",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  background: `${theme.palette.primary.main}11`,
                },
              }}
            >
              See all features{" "}
              <KeyboardArrowDownIcon
                sx={{ fontSize: "1.1rem", ml: 0.5, verticalAlign: "middle" }}
              />
            </Button>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.78rem", letterSpacing: 0.2 }}
          >
            Free to try — 5 searches a day, no credit card needed.
          </Typography>
        </Box>
      </Box>

      {/* ── Comparison: The "expensive" flight insight ── */}
      <motion.div
        variants={{
          hidden: { opacity: 0, y: 40 },
          visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" },
          },
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
      >
        <Box
          sx={{ maxWidth: "900px", mx: "auto", px: 2, mb: { xs: 8, md: 10 } }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: "24px",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(95,90,162,0.35)" : "rgba(95,90,162,0.2)"}`,
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(28,28,53,0.9) 0%, rgba(20,25,46,1) 100%)"
                  : "linear-gradient(135deg, rgba(240,238,255,0.9) 0%, rgba(255,255,255,1) 100%)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Decorative glow */}
            <Box
              sx={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(95,90,162,0.15) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />

            <Typography
              variant="overline"
              sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 2 }}
            >
              The insight that changes how you travel
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, mb: 0.5 }}>
              The "expensive" flight is often the cheaper trip.
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
              Optimizing for cheap flights often leads to expensive stays.
              Here's the math:
            </Typography>

            {/* Comparison cards */}
            <Grid container spacing={2} alignItems="stretch" sx={{ mb: 4 }}>
              {/* Card A — Cheap flight */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    borderRadius: "16px",
                    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: 2.5,
                      py: 1,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.05)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <FlightIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Cheap flight approach
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontWeight: 500 }}
                        >
                          ✈️ Flight
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          Lowest fare
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        $220
                      </Typography>
                    </Box>
                    <Divider sx={{ opacity: 0.3 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontWeight: 500 }}
                        >
                          🏠 Airbnb · 7 nights
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          $140/night — high cost area
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        $980
                      </Typography>
                    </Box>
                    <Divider sx={{ opacity: 0.3 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        Total
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: "1.25rem",
                          color: "primary.main",
                        }}
                      >
                        $1,200
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Card B — Smarter total */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid rgba(95,90,162,0.5)",
                    background:
                      theme.palette.mode === "dark"
                        ? "linear-gradient(160deg, rgba(95,90,162,0.18) 0%, rgba(95,90,162,0.07) 100%)"
                        : "linear-gradient(160deg, rgba(95,90,162,0.1) 0%, rgba(95,90,162,0.03) 100%)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: 2.5,
                      py: 1,
                      background: "linear-gradient(90deg, #2d7a3a, #3a9e4a)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <HotelIcon
                      sx={{ fontSize: 15, color: "rgba(255,255,255,0.85)" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#fff",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Smarter pick — optimise total cost
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontWeight: 500 }}
                        >
                          ✈️ Flight
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          Pricier flight
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        $380
                      </Typography>
                    </Box>
                    <Divider
                      sx={{ opacity: 0.3, borderColor: "rgba(95,90,162,0.4)" }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontWeight: 500 }}
                        >
                          🏠 Airbnb · 7 nights
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          $45/night — affordable destination
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        $315
                      </Typography>
                    </Box>
                    <Divider
                      sx={{ opacity: 0.3, borderColor: "rgba(95,90,162,0.4)" }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "success.main" }}
                      >
                        Total
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: "1.25rem",
                          color: "success.main",
                        }}
                      >
                        $695
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Savings summary */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2.5,
                borderRadius: "14px",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(46,160,67,0.12)"
                    : "rgba(46,160,67,0.08)",
                border: "1px solid rgba(46,160,67,0.25)",
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <TrendingDownIcon
                sx={{ color: "success.main", fontSize: 28, flexShrink: 0 }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    color: "success.main",
                    lineHeight: 1.2,
                  }}
                >
                  You save $505 — by spending more on the flight.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.3 }}
                >
                  That's almost enough for another trip.
                </Typography>
              </Box>
              <Box
                sx={{
                  ml: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: "primary.main" }}
                >
                  $1,200
                </Typography>
                <ArrowForwardIcon
                  sx={{ color: "text.disabled", fontSize: 18 }}
                />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: "success.main" }}
                >
                  $695
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body1"
              sx={{
                fontSize: "1.05rem",
                lineHeight: 1.85,
                color: "text.secondary",
              }}
            >
              This is the whole reason FlyBnB exists — to show you flight and
              stay costs together so you can make the smarter call, instead of
              optimizing one thing while ignoring the other.
            </Typography>
          </Paper>
        </Box>
      </motion.div>

      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          px: 2,
        }}
      >
        {featureData.map((feature, index) => (
          <AnimatedFeatureSection key={index} feature={feature} index={index} />
        ))}
      </Box>
    </Box>
  );
};

export default Explainer;
